import type { LtlCommodity, LtlHandlingUnit } from '../../types/ltl';
import type { WeightUnit } from '../../types/weight';

/**
 * Builds an empty commodity, inheriting the weight unit from an existing
 * commodity so a unit's commodities stay consistent. Falls back to the
 * seller's display weight unit (rather than a hardcoded literal) when there's
 * no existing commodity to inherit from. Adapted from the freight-form's
 * `createEmptyCommodity`.
 */
export const createEmptyCommodity = (existingCommodities: LtlCommodity[], displayWeightUnit: WeightUnit): LtlCommodity => ({
  id: crypto.randomUUID(),
  description: '',
  freightClass: '',
  nmfcCode: '',
  quantity: 1,
  weight: 0,
  weightUnit: existingCommodities[0]?.weightUnit ?? displayWeightUnit,
  isHazmat: false,
  hazmatDetails: null,
  hazmatInfo: null,
  insurance: { isInsured: false, commodityCategoryCode: '', condition: '', declaredValue: 0 },
});

/**
 * Builds an empty handling unit with a single empty commodity, inheriting unit
 * preferences from an existing unit. Adapted from the freight-form's
 * `createEmptyHandlingUnit`.
 */
export const createEmptyHandlingUnit = (existingUnits: LtlHandlingUnit[], displayWeightUnit: WeightUnit): LtlHandlingUnit => {
  const referenceUnit = existingUnits[0];

  return {
    id: crypto.randomUUID(),
    packagingType: 'Pallet',
    quantity: 1,
    length: 0,
    width: 0,
    height: 0,
    dimensionUnit: referenceUnit?.dimensionUnit,
    isStackable: false,
    commodities: [createEmptyCommodity(referenceUnit?.commodities ?? [], displayWeightUnit)],
  };
};
